import { GlobalStore, UserStore } from '@roc/feature-app-core';
import {
  GENERIC_ERROR_MESSAGE,
  isNil,
  LoanSubType,
  segmentAnalytics,
  SegmentTrackerEvent,
} from '@roc/feature-utils';
import { NewLoanApplicationService } from 'libs/feature-borrower-loan-submission/src/modules/newLoanApplication/services/newLoanApplicationService';
import { mapLoanApplicationPayload } from 'libs/feature-borrower-loan-submission/src/modules/newLoanApplication/utils/loanApplicationUtils';
import { flow, makeObservable, override } from 'mobx';
import { LoanService, LoanSubmissionStep } from '@roc/feature-loans';
import { BaseLoanTypeStore } from '../baseLoanTypeStore';
import { SinglePropertyBorrowerInformationStore } from './singlePropertyBorrowerInformationStore';
import { BorrowerEntityInformationStore } from '../borrowerEntityInformationStore';
import { SinglePropertyPropertiesStore } from './singlePropertyPropertiesStore';
import { BorrowerLoanSubmissionService } from '../../services/borrowerLoanSubmissionService';
import { getQuoteFromBorrowerLoanApplication } from 'libs/feature-loans/src/loanSubmission/stores/common/mapLoanToQuoteHelper';
import { OneToolService } from 'libs/feature-one-tool/src/quote/services/oneToolService';
import { mapSinglePropertyLoanPayload } from 'libs/feature-borrower-loan-submission/src/modules/newLoanApplication/utils/borrowerLoanSubmissionUtils';

export class BorrowerSinglePropertyStore extends BaseLoanTypeStore {
  private borrowerLoanSubmissionService: BorrowerLoanSubmissionService;
  private loanService: LoanService;
  private userStore: UserStore;
  private oneToolService: OneToolService;

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    super(globalStore);
    this.isPublic = false;

    this.borrowerInformationStore = new SinglePropertyBorrowerInformationStore(
      globalStore
    );
    this.borrowerEntityInformationStore = new BorrowerEntityInformationStore(
      globalStore,
      this.borrowerInformationStore
    );
    this.propertiesStore = new SinglePropertyPropertiesStore(
      globalStore,
      this.borrowerInformationStore
    );

    this.setDefaults();
    this.borrowerLoanSubmissionService = new BorrowerLoanSubmissionService();
    this.loanService = new LoanService();
    this.userStore = userStore;
    this.oneToolService = new OneToolService();

    makeObservable(this, {
      submitLoan: override,
      submitLoanApplication: flow,
      saveLoanQuoteForApplication: flow,
    });
  }

  *submitLoan() {
    yield this.submitLoanApplication();
  }

  *submitLoanApplication() {
    try {
      if (
        this.globalStore.userFeatures.enableFixFlipPro &&
        this.loanSubtype === LoanSubType.FIX_AND_FLIP
      ) {
        this.loanSubtype = LoanSubType.FIX_AND_FLIP_PRO;
      }
      this.newPublicLoanId = null;
      const data = mapLoanApplicationPayload(
        this.loanSubtype,
        this.borrowerEntityInformationStore.mainEntity,
        this.borrowerInformationStore.borrowers,
        this.propertiesStore.getProperties()
      );
      const response = yield this.borrowerLoanSubmissionService.submitTermLoanApplication(
        data
      );

      if (response.data?.data?.success === false) {
      } else {
        this.newPublicLoanId = response.data?.data?.loanId || response.data?.data?.dealid;
        this.loanBorrowersInfo = response.data?.data?.loanBorrowerInformation?.loanBorrowers?.rows;
        segmentAnalytics.trackEvent({
          name: SegmentTrackerEvent.LOAN_BORROWER_SUBMISSION,
          userName: this.userStore?.userInformation?.username,
        });
      }
    } catch (error) {
      console.log(error);
      this.sendLoanSubmissionErrorEmail(
        error?.error?.response?.data?.error?.message ||
        error?.error?.message ||
        error
      );
    } finally {
      this.setActiveStep(LoanSubmissionStep.SUCCESS);
      this.saved = true;
    }
  }

  sendLoanSubmissionErrorEmail(error: string) {
    this.loanService.sendErrorEmail(
      'Borrower Term Loan Submission Failed',
      `Error occured while submitting ${this.loanType} (${this.loanSubtype}) loan`,
      error || 'Error occured when submitting loan'
    );
  }

  *saveLoanQuoteForApplication(loanSubtype, entity, borrowers, properties) {
    try {
      if (!isNil(this.newPublicLoanId)) {
        const quoteData = {
          ...getQuoteFromBorrowerLoanApplication(this.newPublicLoanId, loanSubtype, entity, borrowers, properties),
          lenderId: this.globalStore.lenderInfo.lenderId,
        };
        const response = yield this.oneToolService.saveQuote(quoteData);
        console.log('Got quote with id', response?.data?.data?.quoteId)
      }
      else {
        console.log('Skipping saveLoanQuote for borrower application because loanId is null');
      }
    }
    catch (error) {
      console.log('Error while saving borrower application as loan quote');
      console.log(error);
    }
  }

  *submitTermLoan() {
    try {
      this.newPublicLoanId = null;
      const currentLoan = {
        loanSubtype: this.loanSubtype,
        submittedFromPortal: this.userStore.userInformation.portalName,
      }
      const data = mapSinglePropertyLoanPayload(
        currentLoan,
        this.borrowerEntityInformationStore.mainEntity,
        this.borrowerInformationStore.borrowers,
        this.propertiesStore.getProperties(),
        null,
        this.userStore,
        null
      );
      const response = yield this.loanService.submitTermLoan(
        data
      );

      if (response.data?.data?.success === true) {
        this.newPublicLoanId = response.data?.data?.loanId;
        this.loanBorrowersInfo = response.data?.data?.loanBorrowerInformation?.loanBorrowers?.rows;
        segmentAnalytics.trackEvent({
          name: SegmentTrackerEvent.BORROWER_LOAN_SUBMISSION,
          userName: this.userStore?.userInformation?.username,
        });
      }
    } catch (error) {
      console.log(error);
      this.sendLoanSubmissionErrorEmail(
        error?.error?.response?.data?.error?.message ||
        error?.error?.message ||
        error
      );
    } finally {
      this.setActiveStep(LoanSubmissionStep.SUCCESS);
      this.saved = true;
    }
  }
}
